import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';
import moment from 'moment-timezone';

import CourtDeskSidePanel from './CourtDeskSidePanel';
import {
    getPositionDisplayNameOfMatch,
    getRoundName,
    hasSchedularStatus,
} from '../../../utils/courtSchedular';
import { TIME_FORMAT_12H } from '../../../utils/formatting';
import { getLabelByTypeId } from '../../../utils/types';

import { RadioButton, Select } from '../../../components/Form';
import { drawPositionTypeIds } from '../../../utils/eventConstants';
import { MATCH_SETTINGS_UPDATE_FAILURE } from '../../../actions/actionTypes/court-scheduler-types';

import styles from './courtSchedular.scss';
import EventDeskPostScore from '../EventDeskPostScore';

const EditMatchSettingsConfig = (props) => {
    const {
        className,
        eventId,
        eventSchedule,
        closeSideBar,
        match: defaultMatch,
        officials,
        sportTypeId,
        utrTypeId,
        clubTierType,
        onPostSubmit,
        isSubmitting,
        savingError,
        playersDictByMemberId,
        teamsDict,
    } = props;

    const dispatch = useDispatch();
    const { scoringTypes } = useSelector(state => state.coreUI.types.tms.division.tournament);
    const postScoreRef = useRef(null);
    const [match, setMatch] = useState(defaultMatch);

    const position1Name = getPositionDisplayNameOfMatch('position1', match, playersDictByMemberId, teamsDict);
    const position2Name = getPositionDisplayNameOfMatch('position2', match, playersDictByMemberId, teamsDict);
    const scoringFormatLabel = getLabelByTypeId(scoringTypes, match.scoringTypeId);
    const [selectedOfficial, setSelectedOfficial] = useState(null);
    const [scoreChanged, setScoreChanged] = useState(false);
    const [scoresAreValid, setScoresAreValid] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const canShowPostScoreSection = useMemo(() => {
        const isPosition1Valid = defaultMatch.position1?.typeId === drawPositionTypeIds.PLAYER ||
            defaultMatch.position1?.typeId === drawPositionTypeIds.TEAM;
        const isPosition2Valid = defaultMatch.position2?.typeId === drawPositionTypeIds.PLAYER ||
            defaultMatch.position2?.typeId === drawPositionTypeIds.TEAM;

        return isPosition1Valid && isPosition2Valid;
    }, [defaultMatch]);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        setSelectedOfficial(defaultMatch.officialId);
    }, [defaultMatch.officialId]);

    const handleRefereeChange = (value) => {
        setSelectedOfficial(value);
    };

    const handleClose = () => {
        setIsVisible(false);
    };

    const onSubmitClick = async () => {
        let postScoreResult;

        if (canShowPostScoreSection) {
            postScoreResult = postScoreRef.current?.getPostScoreResult();

            const hasFailedValidation = postScoreRef.current?.validatePostScore();
            if (hasFailedValidation?.error) {
                dispatch({
                    type: MATCH_SETTINGS_UPDATE_FAILURE,
                    payload: hasFailedValidation?.error,
                });
                return;
            }
        }

        const hasOrderOfPlayChanged = (defaultMatch.includesTime !== match.includesTime) || (defaultMatch.notBefore !== match.notBefore);

        const requestBody = {
            postScoreResult: (canShowPostScoreSection && scoreChanged) ? postScoreResult : null,
            orderOfPlayDraft: hasOrderOfPlayChanged ? {
                eventId,
                matchId: match.id,
                includesTime: match.includesTime,
                notBefore: match.notBefore,
            } : null,
            officialId: selectedOfficial !== match.officialId ? selectedOfficial : null,
        };

        onPostSubmit(eventId, match.id, match, requestBody);
    };

    const isFixedTimeMatch = !match.notBefore && match.includesTime;
    const isNotBefore = match.notBefore && match.includesTime;
    const isFollowedBy = !isFixedTimeMatch && !isNotBefore;

    const onFixedTimeClick = () => {
        setMatch({
            ...match,
            notBefore: false,
            includesTime: true,
        });
    };

    const onFollowedByClick = () => {
        setMatch({
            ...match,
            notBefore: true,
            includesTime: false,
        });
    };

    const onFollowedByNotBeforeClick = () => {
        setMatch({
            ...match,
            notBefore: true,
            includesTime: true,
        });
    };

    const onScoreChanged = () => {
        setScoreChanged(true);
    };

    const hasOrderOfPlayChanged = (defaultMatch.includesTime !== match.includesTime) || (defaultMatch.notBefore !== match.notBefore);
    const hasPostScoreChanged = canShowPostScoreSection && scoreChanged;
    const hasOfficialChanged = selectedOfficial !== defaultMatch.officialId;

    const hasChanges = hasOrderOfPlayChanged || hasPostScoreChanged || hasOfficialChanged;
    const isDisabled = !hasChanges || isSubmitting || (hasPostScoreChanged && !scoresAreValid);

    return (
        <CourtDeskSidePanel
            className={className}
            closeSideBar={handleClose}
            isVisible={isVisible}
            onClose={() => { closeSideBar(); }}
        >
            <div className={styles.header} data-testid="court-scheduler.match-config.title">
                {match.parentDrawName}
            </div>
            <div className={styles.content}>
                <div className={styles.section}>
                    <h6 className={styles.sectionTitle} data-testid="court-scheduler.match-config.draw-round">
                        {`${match.drawName}, ${getRoundName(match)}`}
                    </h6>
                    <div className={styles.matchDetailGroup}>
                        <h6 className={styles.sectionTitle} data-testid="court-scheduler.match-config.players-title">PLAYERS</h6>
                        <div className={styles.matchDetailValue} data-testid="court-scheduler.match-config.player1-name">{position1Name}</div>
                        <div className={styles.matchDetailValue} data-testid="court-scheduler.match-config.player2-name">{position2Name}</div>
                    </div>
                    <div className={styles.matchDetailGroup}>
                        <h6 className={styles.sectionTitle} data-testid="court-scheduler.match-config.scoring-format-title">SCORING FORMAT</h6>
                        <div className={styles.matchDetailValue} data-testid="court-scheduler.match-config.scoring-format">{scoringFormatLabel}</div>
                    </div>
                    {savingError && (
                    <div>
                        <p className={styles.scoreErrorText} data-testid="court-scheduler.match-config.error-title">Error: Invalid score</p>
                        <p
                            className={styles.scoreErrorTextAction}
                            dangerouslySetInnerHTML={{ __html: savingError }}
                            data-testid="court-scheduler.match-config.error-detail"
                        />
                    </div>
                    ) }
                </div>
                <div className={styles.section}>
                    <h6 className={styles.sectionTitle} data-testid="court-scheduler.match-config.preferences-title">
                        Preferences
                    </h6>
                    <div className={styles.radioGroup}>
                        <RadioButton
                            id="fixedTime"
                            controlled
                            label="Fixed time"
                            description="The first match of the day will automatically be a fixed-time match."
                            checked={isFixedTimeMatch}
                            onChange={onFixedTimeClick}
                            disabled={hasSchedularStatus(match)}
                        />
                        {isFixedTimeMatch && (
                            <Select
                                id="matchStartTime"
                                formGroupCssClass={styles.matchStartTime}
                                options={[]}
                                emptyOption={moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).format(TIME_FORMAT_12H)}
                                disabled
                                themeV3
                                controlled={true}
                            />
                        )}
                        <RadioButton
                            id="followed"
                            controlled
                            label="Followed by"
                            checked={isFollowedBy}
                            onChange={onFollowedByClick}
                            disabled={hasSchedularStatus(match)}
                        />
                        <RadioButton
                            id="notBefore"
                            controlled
                            label="Not before"
                            checked={isNotBefore}
                            onChange={onFollowedByNotBeforeClick}
                            disabled={hasSchedularStatus(match)}
                        />
                        {isNotBefore && (
                            <Select
                                id="matchStartTime"
                                formGroupCssClass={styles.matchStartTime}
                                options={[]}
                                emptyOption={moment.utc(match.startTime).tz(eventSchedule?.timeZone?.value).format(TIME_FORMAT_12H)}
                                disabled
                                themeV3
                                controlled={true}
                            />
                        )}
                    </div>
                </div>
                <div className={styles.section}>
                    <h6 className={styles.sectionTitle} data-testid="court-scheduler.match-config.outcome-title">OUTCOME</h6>
                    <Select
                        id="matchReferee"
                        options={officials.map(official => ({
                          label: `${official.firstName} ${official.lastName}`,
                          id: official.id,
                        }))}
                        emptyOption={'Select a referee'}
                        value={selectedOfficial}
                        themeV3
                        controlled={true}
                        onChange={(e) => handleRefereeChange(e)}
                        label="Referee"
                        data-testid="court-scheduler.match-config.referee-select"
                    />
                    {canShowPostScoreSection && (
                        <EventDeskPostScore
                            eventId={eventId}
                            match={match}
                            sportTypeId={sportTypeId}
                            utrTypeId={utrTypeId}
                            playersDictByMemberId={playersDictByMemberId}
                            teamsDict={teamsDict}
                            clubTierType={clubTierType}
                            onScoreChanged={onScoreChanged}
                            onValidateScore={setScoresAreValid}
                            ref={postScoreRef}
                            data-testid="court-scheduler.match-config.post-score"
                        />
                    )}
                </div>
            </div>
            <div className={`${styles.footer} d-flex`}>
                <button
                    className={'btn btn-default mr8 flex-1'}
                    type="button"
                    onClick={handleClose}
                    data-testid="court-scheduler.match-config.cancel-btn"
                >
                    Cancel
                </button>
                <button
                    className={`btn btn-primary flex-1`}
                    onClick={onSubmitClick}
                    type="button"
                    disabled={isDisabled}
                    data-testid="court-scheduler.match-config.save-btn"
                >
                    Save
                </button>
            </div>
        </CourtDeskSidePanel>
    );
};

EditMatchSettingsConfig.propTypes = {
    className: PropTypes.string,
    eventId: PropTypes.number.isRequired,
    closeSideBar: PropTypes.func,
    onPostSubmit: PropTypes.func.isRequired,
    match: PropTypes.objectOf(any).isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    officials: PropTypes.arrayOf(any).isRequired,
    sportTypeId: PropTypes.number.isRequired,
    utrTypeId: PropTypes.number.isRequired,
    clubTierType: PropTypes.objectOf(any).isRequired,
    isSubmitting: PropTypes.bool,
    savingError: PropTypes.string,
    playersDictByMemberId: PropTypes.objectOf(any),
    teamsDict: PropTypes.objectOf(any),
};

EditMatchSettingsConfig.defaultProps = {
    className: '',
    closeSideBar: () => {},
    isSubmitting: false,
    savingError: null,
    playersDictByMemberId: {},
    teamsDict: {},
};

export default EditMatchSettingsConfig;
